import { t } from "./i18n";

// Built-in slash commands, as Slack has: they run here, with the APIs the
// rest of the app uses, before any app's commands.

export interface BuiltIn {
  command: string;
  usage_hint: string;
  description: string;
}

// builtIns are the commands, for the suggestions list.
export const builtIns = (): BuiltIn[] => [
  { command: "/away", usage_hint: "", description: t("Set yourself away, or back") },
  { command: "/invite", usage_hint: "@someone", description: t("Add people to this channel") },
  { command: "/leave", usage_hint: "", description: t("Leave this channel") },
  { command: "/me", usage_hint: t("text"), description: t("Say something in italics") },
  { command: "/mute", usage_hint: "", description: t("Mute this channel, or unmute it") },
  { command: "/remind", usage_hint: t("me to … in 20 minutes"), description: t("Set a reminder") },
  { command: "/shrug", usage_hint: t("text"), description: t("Add ¯\\_(ツ)_/¯ to a message") },
  { command: "/status", usage_hint: t(":emoji: text, or clear"), description: t("Set your status") },
  { command: "/task", usage_hint: t("capability"), description: t("Ask an agent for work, with a form") },
  { command: "/topic", usage_hint: t("text"), description: t("Set this channel's topic") },
];

const units: Record<string, number> = { m: 60_000, min: 60_000, mins: 60_000, minute: 60_000, minutes: 60_000, h: 3_600_000, hr: 3_600_000, hrs: 3_600_000, hour: 3_600_000, hours: 3_600_000, d: 86_400_000, day: 86_400_000, days: 86_400_000 };

// parseRemind reads "/remind" text: what to be reminded of, and when, from
// "in 20 minutes", "tomorrow", "at 3pm" or "tomorrow at 9:30". A leading
// "me" or "me to" is dropped. null when no time is given, or it is past.
export function parseRemind(text: string, now = new Date()): { what: string; at: Date } | null {
  let rest = ` ${text.trim()} `;
  let at: Date | null = null;
  const take = (re: RegExp) => {
    const m = re.exec(rest);
    if (m) rest = rest.slice(0, m.index) + " " + rest.slice(m.index + m[0].length);
    return m;
  };
  const inN = take(/\s+in\s+(\d+)\s*([a-z]+)\s+/i);
  if (inN && units[inN[2].toLowerCase()]) at = new Date(now.getTime() + Number(inN[1]) * units[inN[2].toLowerCase()]);
  const tomorrow = take(/\s+tomorrow\s+/i);
  const clock = take(/\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+/i);
  if (!at && (tomorrow || clock)) {
    const d = new Date(now);
    if (tomorrow) d.setDate(d.getDate() + 1);
    let h = clock ? Number(clock[1]) : 9;
    const min = clock?.[2] ? Number(clock[2]) : 0;
    const half = clock?.[3]?.toLowerCase();
    if (half === "pm" && h < 12) h += 12;
    if (half === "am" && h === 12) h = 0;
    if (h > 23 || min > 59) return null;
    d.setHours(h, min, 0, 0);
    // "at 9" already past today means tomorrow.
    if (!tomorrow && d <= now) d.setDate(d.getDate() + 1);
    at = d;
  }
  const what = rest
    .trim()
    .replace(/^me\s+(to\s+)?/i, "")
    .trim();
  if (!at || !what || at <= now) return null;
  return { what, at };
}

// parseStatus reads "/status" text: an optional :emoji: first, then the
// words; "clear" clears it.
export function parseStatus(text: string): { emoji: string; text: string } {
  const s = text.trim();
  if (s.toLowerCase() === "clear") return { emoji: "", text: "" };
  const m = /^:([a-z0-9_+-]+):\s*(.*)$/i.exec(s);
  return m ? { emoji: m[1], text: m[2] } : { emoji: "", text: s };
}

// matchCapability finds the capability "/task" names: its exact name, or
// the one name that starts with or contains what was typed, in any case.
export function matchCapability(names: string[], typed: string): string | undefined {
  const q = typed.trim().toLowerCase();
  if (!q) return undefined;
  const exact = names.find((n) => n.toLowerCase() === q);
  if (exact) return exact;
  for (const test of [(n: string) => n.toLowerCase().startsWith(q), (n: string) => n.toLowerCase().includes(q)]) {
    const found = names.filter(test);
    if (found.length) return found.length === 1 ? found[0] : undefined;
  }
  return undefined;
}
